import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Alert,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';

/**
 * ReviewRequestModal Component
 * Modal for confirming therapist review request with optional message
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Handler for closing modal
 * @param {Function} props.onConfirm - Handler for confirming review request
 * @param {boolean} props.loading - Whether request is in progress
 * @param {Object} props.rateLimitInfo - Rate limit information (canRequest, nextAvailable)
 */
const ReviewRequestModal = ({ 
  open, 
  onClose, 
  onConfirm, 
  loading = false,
  rateLimitInfo = { canRequest: true, nextAvailable: null }
}) => {
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (message.length > 500) {
      setError('Message must be 500 characters or less');
      return;
    }
    onConfirm(message.trim());
  };

  const handleClose = () => {
    setMessage('');
    setError('');
    onClose();
  };

  const handleMessageChange = (e) => {
    const value = e.target.value;
    setMessage(value);
    if (value.length <= 500) {
      setError('');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupervisorAccountIcon color="secondary" />
            <Typography variant="h6">Request Therapist Review</Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {!rateLimitInfo.canRequest ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have already requested a review today. You can request another review {rateLimitInfo.nextAvailable}.
          </Alert>
        ) : (
          <>
            <Typography variant="body1" paragraph>
              This will notify your assigned therapist that you would like them to review your conversation and provide guidance.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Your therapist will be notified via email and will review your conversation as soon as possible. They may:
            </Typography>
            
            <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
              <Typography component="li" variant="body2">
                Review your conversation history
              </Typography>
              <Typography component="li" variant="body2">
                Provide personalized feedback and suggestions
              </Typography>
              <Typography component="li" variant="body2">
                Adjust your treatment plan if needed
              </Typography>
              <Typography component="li" variant="body2">
                Take over the conversation temporarily if appropriate
              </Typography>
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Optional: Add a message for your therapist
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                placeholder="Let your therapist know what you'd like them to focus on..."
                value={message}
                onChange={handleMessageChange}
                error={!!error}
                helperText={error || `${message.length}/500 characters`}
                disabled={loading}
              />
            </Box>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> You can request a therapist review once per day. Use this feature when you need additional human support or guidance.
              </Typography>
            </Alert>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="secondary"
          disabled={loading || !rateLimitInfo.canRequest}
        >
          {loading ? 'Sending...' : 'Send Review Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewRequestModal;